import React, { useCallback, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import { CloudUpload, Check, Error } from "@mui/icons-material";
import { BulkStepProps, BulkProjectEntry } from "../../types";

interface CSVValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  requiredColumns: string[];
  missingColumns: string[];
  parsedData: BulkProjectEntry[];
  totalLines: number;
  skippedLines: number;
}

const CSVUploadStep: React.FC<BulkStepProps> = ({
  onUpdateData,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [validation, setValidation] = useState<CSVValidationResult | null>(null);

  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    let i = 0;

    while (i < line.length) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          // Handle escaped quotes ("")
          current += '"';
          i += 2;
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
          i++;
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        result.push(current.trim());
        current = '';
        i++;
      } else {
        current += char;
        i++;
      }
    }
    
    // Add the last field
    result.push(current.trim());
    return result;
  };

  const getErrorMessage = (error: unknown): string => {
    if (typeof error === 'string') return error;
    if (error && typeof error === 'object' && 'message' in error) {
      return (error as Error).message;
    }
    return 'Invalid data';
  };

  const parseCSV = (content: string): { entries: BulkProjectEntry[], warnings: string[], totalLines: number, skippedLines: number } => {
    const lines = content.trim().split('\n').filter(line => line.trim().length > 0);
    if (lines.length < 2) return { entries: [], warnings: [], totalLines: 0, skippedLines: 0 };

    const headers = parseCSVLine(lines[0]).map(h => h.trim());
    const rows = lines.slice(1);
    const entries: BulkProjectEntry[] = [];
    const warnings: string[] = [];
    let skippedLines = 0;

    rows.forEach((row, rowIndex) => {
      try {
        const values = parseCSVLine(row);
        const entry: BulkProjectEntry = {} as BulkProjectEntry;
        
        headers.forEach((header, index) => {
          const value = values[index] || '';
          if (header.toLowerCase().includes('name')) {
            entry.name = value;
          } else if (header.toLowerCase().includes('lat')) {
            const lat = parseFloat(value);
            if (isNaN(lat)) throw `Invalid latitude: ${value}`;
            entry.lat = lat;
          } else if (header.toLowerCase().includes('lng') || header.toLowerCase().includes('lon')) {
            const lng = parseFloat(value);
            if (isNaN(lng)) throw `Invalid longitude: ${value}`;
            entry.lng = lng;
          } else {
            entry[header] = value;
          }
        });

        // Basic validation - must have name and valid coordinates
        if (!entry.name || entry.name.trim() === '') {
          throw 'Missing project name';
        }
        if (isNaN(entry.lat) || entry.lat < -90 || entry.lat > 90) {
          throw `Invalid latitude: ${entry.lat}`;
        }
        if (isNaN(entry.lng) || entry.lng < -180 || entry.lng > 180) {
          throw `Invalid longitude: ${entry.lng}`;
        }

        entries.push(entry);
      } catch (error: unknown) {
        skippedLines++;
        warnings.push(`Row ${rowIndex + 2}: ${getErrorMessage(error)} - line skipped`);
      }
    });

    return { entries, warnings, totalLines: rows.length, skippedLines };
  };

  const validateCSV = (parseResult: { entries: BulkProjectEntry[], warnings: string[], totalLines: number, skippedLines: number }): CSVValidationResult => {
    const errors: string[] = [];
    const requiredColumns = ['name', 'lat', 'lng'];
    const missingColumns: string[] = [];
    const { entries, warnings, totalLines, skippedLines } = parseResult;

    if (totalLines === 0) {
      errors.push('CSV file is empty or could not be parsed');
      return {
        isValid: false,
        errors,
        warnings: [],
        requiredColumns,
        missingColumns,
        parsedData: [],
        totalLines: 0,
        skippedLines: 0
      };
    }

    if (entries.length === 0) {
      errors.push('All rows were invalid and skipped. Please check your CSV format.');
    }

    // Check if we have any valid entries
    if (entries.length > 0) {
      const firstRow = entries[0];
      requiredColumns.forEach(col => {
        if (!(col in firstRow)) {
          missingColumns.push(col);
        }
      });

      if (missingColumns.length > 0) {
        errors.push(`Missing required columns: ${missingColumns.join(', ')}`);
      }
    }

    return {
      isValid: errors.length === 0 && entries.length > 0,
      errors,
      warnings,
      requiredColumns,
      missingColumns,
      parsedData: entries,
      totalLines,
      skippedLines
    };
  };

  const handleFileUpload = useCallback(async (file: File) => {
    try {
      const content = await file.text();
      const parseResult = parseCSV(content);
      const validationResult = validateCSV(parseResult);
      
      setValidation(validationResult);
      
      if (validationResult.isValid) {
        onUpdateData({
          csvFile: file,
          parsedProjects: validationResult.parsedData
        });
      }
    } catch (error) {
      setValidation({
        isValid: false,
        errors: ['Failed to read CSV file'],
        warnings: [],
        requiredColumns: ['name', 'lat', 'lng'],
        missingColumns: [],
        parsedData: [],
        totalLines: 0,
        skippedLines: 0
      });
    }
  }, [onUpdateData]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
        handleFileUpload(file);
      } else {
        setValidation({
          isValid: false,
          errors: ['Please select a valid CSV file'],
          warnings: [],
          requiredColumns: ['name', 'lat', 'lng'],
          missingColumns: [],
          parsedData: [],
          totalLines: 0,
          skippedLines: 0
        });
      }
    }
  }, [handleFileUpload]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  }, [handleFileUpload]);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ p: 3, backgroundColor: "#f8f9fa", borderBottom: "1px solid #e0e0e0" }}>
        <Typography
          variant="h5"
          sx={{
            mb: 1,
            fontWeight: 600,
            color: "#1a1a1a",
          }}
        >
          Upload Project List
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#666",
          }}
        >
          Upload a CSV file containing your project locations and details
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
        <Box sx={{ maxWidth: 800, mx: "auto" }}>
          {/* CSV Format Instructions */}
          <Card sx={{ mb: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                CSV Format Requirements
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
                Your CSV file must include the following columns:
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                <Chip label="Project Name" color="primary" variant="outlined" />
                <Chip label="lat (latitude)" color="primary" variant="outlined" />
                <Chip label="lng (longitude)" color="primary" variant="outlined" />
              </Box>
              <Typography variant="body2" sx={{ color: "#666", fontStyle: "italic" }}>
                Additional columns will be preserved and can be viewed during review.
                Column names are case-insensitive.
              </Typography>
            </CardContent>
          </Card>

          {/* File Upload Area */}
          <Card sx={{ mb: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <CardContent sx={{ p: 3 }}>
              <Box
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => document.getElementById('csv-upload')?.click()}
                sx={{
                  border: `2px dashed ${dragActive ? "#00d4ad" : "#e0e0e0"}`,
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                  backgroundColor: dragActive ? "#f0fdf8" : "#fafafa",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#00d4ad",
                    backgroundColor: "#f0fdf8",
                  }
                }}
              >
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                  id="csv-upload"
                />
                <CloudUpload sx={{ fontSize: 48, color: "#00d4ad", mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  {dragActive ? "Drop CSV file here" : "Upload CSV File"}
                </Typography>
                <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
                  Drag and drop your CSV file here, or click to select
                </Typography>
                <Button
                  variant="contained"
                  onClick={(e) => {
                    e.stopPropagation();
                    document.getElementById('csv-upload')?.click();
                  }}
                  sx={{
                    backgroundColor: "#00d4ad",
                    "&:hover": { backgroundColor: "#00b89c" },
                  }}
                >
                  Choose File
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Validation Results */}
          {validation && (
            <Card sx={{ mb: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  {validation.isValid ? (
                    <Check sx={{ color: "#4caf50", mr: 1 }} />
                  ) : (
                    <Error sx={{ color: "#f44336", mr: 1 }} />
                  )}
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Validation {validation.isValid ? "Successful" : "Failed"}
                  </Typography>
                </Box>

                {!validation.isValid && validation.errors.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {validation.errors.map((error, index) => (
                      <Alert key={index} severity="error" sx={{ mb: 1 }}>
                        {error}
                      </Alert>
                    ))}
                  </Box>
                )}

                {validation.warnings.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      {validation.skippedLines} of {validation.totalLines} rows were skipped due to invalid data:
                    </Alert>
                    {validation.warnings.slice(0, 5).map((warning, index) => (
                      <Alert key={index} severity="warning" sx={{ mb: 1, fontSize: '0.875rem' }}>
                        {warning}
                      </Alert>
                    ))}
                    {validation.warnings.length > 5 && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        ... and {validation.warnings.length - 5} more warnings
                      </Alert>
                    )}
                  </Box>
                )}

                {validation.isValid && validation.parsedData.length > 0 && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Successfully parsed {validation.parsedData.length} projects
                    {validation.skippedLines > 0 && ` (${validation.skippedLines} rows skipped)`}
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* Data Preview */}
          {validation?.isValid && validation.parsedData.length > 0 && (
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Data Preview ({validation.parsedData.length} projects)
                </Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Project Name</TableCell>
                        <TableCell>Latitude</TableCell>
                        <TableCell>Longitude</TableCell>
                        {Object.keys(validation.parsedData[0])
                          .filter(key => !['name', 'lat', 'lng'].includes(key))
                          .map(key => (
                            <TableCell key={key}>{key}</TableCell>
                          ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validation.parsedData.slice(0, 10).map((project, index) => (
                        <TableRow key={index}>
                          <TableCell>{project.name}</TableCell>
                          <TableCell>{project.lat.toFixed(6)}</TableCell>
                          <TableCell>{project.lng.toFixed(6)}</TableCell>
                          {Object.entries(project)
                            .filter(([key]) => !['name', 'lat', 'lng'].includes(key))
                            .map(([key, value]) => (
                              <TableCell key={key}>{String(value)}</TableCell>
                            ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {validation.parsedData.length > 10 && (
                  <Typography variant="caption" sx={{ mt: 1, color: "#666", display: "block" }}>
                    Showing first 10 projects. All {validation.parsedData.length} projects will be processed.
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CSVUploadStep;
